<template>
  <v-card
    flat
  >
  <!-- Purchase Toolbar -->
  <v-toolbar
    v-if="!success"
    dark
    color="primary"
  >
    <v-btn
      icon
      dark
      @click="clearAndClose()"
    >
      <v-icon>mdi-close</v-icon>
    </v-btn>
    <v-toolbar-title>Purchase Shares</v-toolbar-title>
  </v-toolbar>
  <!-- Successful purchase toolbar -->
  <v-toolbar
    v-else
    dark
    color="green"
  >
    <v-btn
      icon
      dark
      @click="clearAndClose()"
    >
      <v-icon>mdi-close</v-icon>
    </v-btn>
    <v-toolbar-title>Congratulations!</v-toolbar-title>
  </v-toolbar>

  <v-card-title v-if="!success">{{ flipAsset.title }}</v-card-title>
  <v-card-title v-else>You've successfully purchased {{ shares }} shares of <br> {{ flipAsset.title }}</v-card-title>
  <v-card-subtitle class="mt-1 pb-3" v-if="purchaseAbility.ach"><strong>Since you're executing an ACH, this transaction may be held until your bank verifies the transfer.  This could take 3 - 5 business days</strong></v-card-subtitle>
  <v-card-subtitle class="mt-1 pb-3" v-if="purchaseAbility.externalTransferAmount > 0"><strong>The shares you tried to buy exceed the merchant's limit. They've been placed on hold in your account. We'll reach out for an offline payment to finalize the transaction. Please verify your phone number in your profile or reach us promptly at <a href="tel:512-813-0636">512-813-0636</a>.</strong></v-card-subtitle>
  <v-card-subtitle class="mt-1 pb-3" v-if="!success"><strong>{{ purchaseAbility.currentWalletBalance | numberToMoney }}</strong> available in wallet.</v-card-subtitle>
  <v-card-subtitle class="pb-3" v-if="!success">You own <strong>{{ purchaseAbility.sharesAlreadyOwnedByUser }}</strong> of <strong>{{ flipAsset.totalShares }}</strong> shares of <strong>{{ flipAsset.title }}</strong>.</v-card-subtitle>

    <v-divider class="mx-3"></v-divider>

    <v-card-text v-if="!success">
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
      >
        <v-row>
          <v-col cols="8" class="pa-0">
            <v-subheader class="mt-2">Number of Shares to Purchase</v-subheader>
          </v-col>
          <v-col cols="4" class="py-0 px-3">
            <v-text-field
              label="Shares"
              v-model="shares"
              :hint="purchaseAbility.sharesAvailableForSale + ' available'"
              :rules="[rules.number]"
              @input="checkAbility"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="8" class="pa-0">
            <v-subheader>Price Per Share</v-subheader>
          </v-col>
          <v-col cols="4" class="pa-0">
            <v-subheader>{{ purchaseAbility.pricePerShare | numberToMoney }}</v-subheader>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="8" class="pa-0">
            <v-subheader>Total Share Price</v-subheader>
          </v-col>
          <v-col cols="4" class="pa-0">
            <v-subheader>{{ purchaseAbility.totalSharePrice | numberToMoney }}</v-subheader>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="8" class="pa-0">
            <v-subheader>BCC Fee</v-subheader>
          </v-col>
          <v-col cols="4" class="pa-0">
            <v-subheader>{{ purchaseAbility.transferFeeAmount | numberToMoney }}</v-subheader>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="8" class="pa-0">
            <v-subheader>Total Cost</v-subheader>
          </v-col>
          <v-col cols="4" class="pa-0">
            <v-subheader>{{ purchaseAbility.totalPurchaseCharge | numberToMoney }}</v-subheader>
          </v-col>
        </v-row>
        <v-row v-if="purchaseAbility.fundInRequired">
          <v-col cols="8" class="pa-0">
            <v-subheader class="font-weight-bold">Additional Funds Required</v-subheader>
          </v-col>
          <v-col cols="4" class="pa-0">
            <v-subheader class="font-weight-bold">{{ purchaseAbility.fromFundIn | numberToMoney }}</v-subheader>
          </v-col>
        </v-row>
        <v-row v-if="purchaseAbility.fundInRequired">
          <p><em>A 1.53% Merchant fee will be applied to all Credit Card Transactions</em></p>
        </v-row>
        <v-row v-if="purchaseAbility.externalTransferAmount > 0">
          <v-col cols="8" class="pa-0">
            <v-subheader class="font-weight-bold">External Payment Required</v-subheader>
          </v-col>
          <v-col cols="4" class="pa-0">
            <v-subheader class="font-weight-bold">{{ purchaseAbility.externalTransferAmount | numberToMoney }}</v-subheader>
          </v-col>
        </v-row>
        <v-row v-if="purchaseAbility.externalTransferAmount > 0">
          <p><em>You will need to mail your payment of {{ purchaseAbility.externalTransferAmount | numberToMoney }} to BlueChipCar to complete your purchase</em></p>
        </v-row>
        <IntegrationSelect
          v-if="purchaseAbility.fundInRequired"
          v-model="integrationId"
        ></IntegrationSelect>
      </v-form>
    </v-card-text>

    <v-divider class="mx-3" v-if="!success"></v-divider>

    <v-card-actions class="pa-0 ma-0" v-if="!success">
      <v-card-subtitle
        v-if="error"
        class="red--text"
      >
      {{ orderMessage() }}
      </v-card-subtitle>
      <v-card-subtitle v-else >
        You are placing an order to buy <strong>{{ shares }}</strong> shares of <strong>{{ flipAsset.title }}</strong> for <strong>{{ purchaseAbility.totalPurchaseCharge ? purchaseAbility.totalPurchaseCharge : '0' | numberToMoney }}</strong>.
      </v-card-subtitle>
      <v-btn
        color="primary"
        :loading="loading"
        :disabled="!canPurchase || !shares || !(purchaseAbility.fundInRequired ? integrationId !== '' : true)"
        @click="buyShares"
        class="mr-3"
      >
        Purchase
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import IntegrationSelect from './IntegrationSelect.vue'

  export default {
    name: 'PurchaseShareForm',

    components: {
      IntegrationSelect
    },

    computed: {
      ...mapGetters([
        'user',
        'flipAsset',
        'purchaseAbility'
      ])
    },

    data: () => ({
      error: false,
      shares: '',
      canPurchase: false,
      integrationId: '',

      // Form handling
      loading: false,
      valid: true,
      success: false,

      // validation rules
      rules: {
        number: value => {
          return !!value && Number.isInteger(Number(value)) || 'Must be a whole number.'
        }
      }
    }),

    methods: {
      ...mapActions([
        'purchaseShares',
        'prePurchaseCheck',
        'clearPurchaseAbility',
        'getFlipAssetById'
      ]),

      checkAbility: function() {
        // Sanity check before we start hitting the API.
        if (this.rules.number(this.shares) === true || !this.shares) {
          // Build up a query.
          let queryPayload = {
            numberOfSharesToPurchase: !this.shares ? 0 : this.shares,
            //userIntegrationId: this.user.id
          }

          // Call the check to fetch purchase availability.
          this.prePurchaseCheck({
            assetId: this.flipAsset.id,
            query: queryPayload
          }).then(() => {
            this.canPurchase = this.purchaseAbility.reason !== 14;

            this.error = !this.purchaseAbility.purchaseAllowed
          })
        }
      },

      orderMessage: function() {
        switch(this.purchaseAbility.reason) {
          case 14:
            return 'There are not enough shares to purchase, or you will own more that 20%.'
          case 8:
            return `Your selected payment method will be charged $${this.purchaseAbility.fromFundIn} plus any applicable merchant fees to satisfy the required additional funds to make this purchase.`
        }
      },

      buyShares: function() {
        this.loading = true

        // Check validation
        this.$refs.form.validate()

        if (this.valid === true) {
          console.log(`attempting purchase of ${this.shares} shares`)
          console.log(`for asset ${this.flipAsset.id}`)
          console.log(`and user ${this.user.id}`)

          let purchasePayload = {
            numberOfSharesToPurchase: this.shares,
            userIntegrationId: this.integrationId
          }

          // Purchase shares.
          this.purchaseShares({
            assetId: this.flipAsset.id,
            payload: purchasePayload
          }).then(async () => {
            // Success! Close everything out.
            console.log(`Successfully purchased ${this.shares} shares for asset ${this.flipAsset.id}.`)
            this.loading = false
            this.success = true
            //this.$refs.form.reset()
            //this.$emit('dialog-close')
          })
        }
        else {
          this.loading = false
        }
      },

      clearAndClose: function() {
        this.success = false
        this.$emit('dialog-close')
      },

      dialogOpen: function() {
        this.$refs.form.reset()
        this.checkAbility()
      }
    },

    mounted() {
      this.success = false
      this.$refs.form.reset()
      this.checkAbility()
    }
  }
</script>
